import { useState } from "react";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSelect from "../../system-design/DSSelect";
import DSMenuItem from "../../system-design/DSMenuItem";
import DSFormControl from "../../system-design/DSFormControl";
import DSInputLabel from "../../system-design/DSInputLabel";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import { MapBounds } from "../../../api/admin";
import CollectionStatsContent from "./CollectionStatsContent";
import KlydosStatsContent from "./KlydosStatsContent";

const LIMIT_OPTIONS = [5, 10, 15] as const;
const DEFAULT_LIMIT = 10;

type ViewMode = "current" | "global";
type TabValue = "collections" | "klydos";

type StatsDialogProps = {
  open: boolean;
  onClose: () => void;
  bounds: MapBounds;
};

export const MEDIA_STYLE = {
  width: 50,
  height: 50,
  objectFit: "cover" as const,
  borderRadius: 4,
};

export const ID_CELL_STYLE = {
  fontFamily: "monospace",
  fontSize: "12px",
  color: "text.secondary",
};

export const LoadingState = () => (
  <DSBox
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      py: 4,
    }}
  >
    <DSCircularProgress />
  </DSBox>
);

export const ErrorState = ({ message }: { message: string }) => (
  <DSBox sx={{ color: "error.main", py: 2 }}>
    Error loading data: {message}
  </DSBox>
);

export const EmptyState = ({
  isGlobal,
  type,
}: {
  isGlobal: boolean;
  type: string;
}) => (
  <DSBox sx={{ color: "text.secondary", py: 2, textAlign: "center" }}>
    No {type} found {isGlobal ? "" : "in this area"}
  </DSBox>
);

export const SummaryBar = ({
  itemCount,
  totalCount,
  tabLabel,
}: {
  itemCount: number;
  totalCount: number;
  tabLabel: string;
}) => (
  <DSBox
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
      px: 1,
    }}
  >
    <DSTypography variant="body2" color="text.secondary">
      Showing top {itemCount} of {tabLabel}
    </DSTypography>
    <DSBox
      sx={{
        px: 2,
        py: 0.5,
        backgroundColor: "primary.main",
        color: "white",
        borderRadius: 1,
        fontWeight: "bold",
      }}
    >
      Total: {totalCount}
    </DSBox>
  </DSBox>
);

const StatsDialog = ({ open, onClose, bounds }: StatsDialogProps) => {
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [viewMode, setViewMode] = useState<ViewMode>("current");
  const [activeTab, setActiveTab] = useState<TabValue>("collections");

  const isGlobal = viewMode === "global";

  const handleLimitChange = (event: { target: { value: unknown } }) => {
    setLimit(event.target.value as number);
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DSDialogTitle>Stats</DSDialogTitle>
      <DSDialogContent>
        <DSBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2,
            mt: 1,
            flexWrap: "wrap",
          }}
        >
          <DSBox sx={{ display: "flex", gap: 0.5 }}>
            <DSPrimaryButton
              onClick={() => setViewMode("current")}
              size="small"
              variant={viewMode === "current" ? "contained" : "outlined"}
            >
              Current View
            </DSPrimaryButton>
            <DSPrimaryButton
              onClick={() => setViewMode("global")}
              size="small"
              variant={viewMode === "global" ? "contained" : "outlined"}
            >
              Global
            </DSPrimaryButton>
          </DSBox>

          <DSFormControl size="small" sx={{ minWidth: 120 }}>
            <DSInputLabel>Limit</DSInputLabel>
            <DSSelect value={limit} label="Limit" onChange={handleLimitChange}>
              {LIMIT_OPTIONS.map((option) => (
                <DSMenuItem key={option} value={option}>
                  {option}
                </DSMenuItem>
              ))}
            </DSSelect>
          </DSFormControl>
        </DSBox>

        <DSBox
          sx={{
            display: "flex",
            gap: 1,
            borderBottom: 1,
            borderColor: "divider",
            mb: 2,
            pb: 1,
          }}
        >
          <DSPrimaryButton
            onClick={() => setActiveTab("collections")}
            size="small"
            variant={activeTab === "collections" ? "contained" : "text"}
          >
            Top Collections
          </DSPrimaryButton>
          <DSPrimaryButton
            onClick={() => setActiveTab("klydos")}
            size="small"
            variant={activeTab === "klydos" ? "contained" : "text"}
          >
            Top Klydos
          </DSPrimaryButton>
        </DSBox>

        {activeTab === "collections" && (
          <CollectionStatsContent
            bounds={bounds}
            limit={limit}
            open={open}
            isGlobal={isGlobal}
          />
        )}

        {activeTab === "klydos" && (
          <KlydosStatsContent
            bounds={bounds}
            limit={limit}
            open={open}
            isGlobal={isGlobal}
          />
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSPrimaryButton onClick={onClose} variant="outlined">
          Close
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
};

export default StatsDialog;
